/**
 * Shared React providers for web and desktop (prompt.md §40, §41, §71).
 *
 * WHY the API client and the session are separate contexts: the HTTP client is
 * long-lived and reads its token and workspace lazily, while the session changes
 * on every sign-in, account switch and workspace switch. The client therefore
 * reads the active workspace through a ref that the session keeps up to date, so
 * a request already in flight is never silently retargeted at another workspace
 * by a re-render.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import type { WorkspaceId } from "@dayvise/domain";
import { DayviseApiError, type DayviseClient, createDayviseClient } from "@dayvise/client-sdk";

import {
  SIGNED_OUT,
  type SessionAccount,
  type SessionState,
  type SessionWorkspace,
  type TokenStore,
  MemoryTokenStore,
  defaultWorkspaceFor,
  planAccountSwitch,
  planWorkspaceSwitch,
} from "./session.ts";
import { keyspacesToQueryRoots } from "./queryKeys.ts";

export interface DayviseClientConfigInput {
  readonly baseUrl: string;
  readonly fetch?: typeof fetch;
  readonly defaultHeaders?: Readonly<Record<string, string>>;
}

interface ClientContextValue {
  readonly client: DayviseClient;
  readonly tokenStore: TokenStore;
  readonly setActiveWorkspace: (workspaceId: WorkspaceId | null) => void;
  /**
   * Register the reaction to a rejected credential.
   *
   * The client is created here but the session lives in `SessionProvider`, so the reaction
   * has to be handed in: only the session knows how to end itself.
   */
  readonly setOnUnauthorized: (react: (() => void) | null) => void;
}

const ClientContext = createContext<ClientContextValue | null>(null);

export interface DayviseProviderProps {
  readonly config: DayviseClientConfigInput;
  readonly tokenStore?: TokenStore;
  readonly queryClient?: QueryClient;
  readonly children: ReactNode;
}

export function DayviseProvider({ config, tokenStore, queryClient, children }: DayviseProviderProps) {
  const store = useMemo(() => tokenStore ?? new MemoryTokenStore(), [tokenStore]);
  const workspaceRef = useRef<WorkspaceId | null>(null);

  // The reaction to a rejected credential, registered by `SessionProvider`.
  //
  // The client outlives every session, so the two cannot be wired at construction. A 401
  // says the credential the client is holding is no longer accepted, and acting on that is
  // the session's job rather than the client's.
  const onUnauthorized = useRef<(() => void) | null>(null);

  const client = useMemo(
    () =>
      createDayviseClient({
        baseUrl: config.baseUrl,
        getToken: () => store.read(),
        getWorkspace: () => workspaceRef.current,
        fetch: config.fetch,
        defaultHeaders: config.defaultHeaders,
        // Without this a 401 left the app `signed_in` holding a dead token: every screen
        // rendered an error, the token stayed in storage, and only a manual reload
        // recovered. `status === 0` is a network failure and deliberately signs nobody
        // out, because an unreachable server says nothing about the credential.
        onResponse: (observation) => {
          if (observation.status === 401) onUnauthorized.current?.();
        },
      }),
    [config.baseUrl, config.fetch, config.defaultHeaders, store],
  );

  const cache = useMemo(
    () =>
      queryClient ??
      new QueryClient({
        defaultOptions: {
          queries: {
            // A run continues server-side while a client is disconnected, so a
            // short stale window keeps a reconnecting client honest without
            // polling in a tight loop.
            staleTime: 15_000,
            retry: 1,
          },
        },
      }),
    [queryClient],
  );

  const setActiveWorkspace = useCallback((workspaceId: WorkspaceId | null) => {
    workspaceRef.current = workspaceId;
  }, []);

  const setOnUnauthorized = useCallback((react: (() => void) | null) => {
    onUnauthorized.current = react;
  }, []);

  const value = useMemo<ClientContextValue>(
    () => ({ client, tokenStore: store, setActiveWorkspace, setOnUnauthorized }),
    [client, store, setActiveWorkspace, setOnUnauthorized],
  );

  return (
    <QueryClientProvider client={cache}>
      <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
    </QueryClientProvider>
  );
}

export function useDayviseClient(): DayviseClient {
  const value = useContext(ClientContext);
  if (value === null) throw new Error("useDayviseClient must be used inside <DayviseProvider>");
  return value.client;
}

export function useTokenStore(): TokenStore {
  const value = useContext(ClientContext);
  if (value === null) throw new Error("useTokenStore must be used inside <DayviseProvider>");
  return value.tokenStore;
}

/**
 * Bind the active workspace to the API client. Called by `SessionProvider`, so
 * every request made through the shared client carries the workspace the user is
 * actually looking at.
 */
export function useWorkspaceBinding(workspaceId: WorkspaceId | null): void {
  const value = useContext(ClientContext);
  const setActiveWorkspace = value?.setActiveWorkspace;
  useEffect(() => {
    setActiveWorkspace?.(workspaceId);
  }, [setActiveWorkspace, workspaceId]);
}

/**
 * Register the reaction to a rejected credential.
 *
 * Called by `SessionProvider`, which owns the state a 401 has to end. The callback has to
 * be stable across renders: a fresh closure would unregister and re-register on every one.
 */
export function useUnauthorizedBinding(react: (() => void) | null): void {
  const value = useContext(ClientContext);
  const setOnUnauthorized = value?.setOnUnauthorized;
  useEffect(() => {
    setOnUnauthorized?.(react);
    return () => setOnUnauthorized?.(null);
  }, [react, setOnUnauthorized]);
}

interface SessionContextValue {
  readonly session: SessionState;
  readonly signIn: (input: { token: string; account: SessionAccount; workspaces: readonly SessionWorkspace[] }) => Promise<void>;
  readonly signOut: () => Promise<void>;
  readonly switchAccount: (account: SessionAccount) => Promise<void>;
  readonly switchWorkspace: (workspaceId: WorkspaceId) => Promise<void>;
  readonly setAccounts: (accounts: readonly SessionAccount[]) => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * Owns the signed-in identity and enforces the cross-plane clearing rule.
 *
 * Every switch computes an invalidation plan from `session.ts` and applies it
 * before the new context renders. Workspace-scoped query data is *removed*, not
 * merely marked stale, because a stale value would still be readable by a screen
 * that renders during the switch — and after a plane change that value belonged
 * to a different organisation.
 */
export function SessionProvider({ children }: { readonly children: ReactNode }) {
  const [session, setSession] = useState<SessionState>(SIGNED_OUT);
  const tokenStore = useTokenStore();
  const client = useDayviseClient();
  const queryClient = useQueryClient();

  useWorkspaceBinding(session.workspace?.workspaceId ?? null);

  // End the session when the server rejects the credential. Registered here rather than
  // handled where a request is made, because a 401 on ANY request means the token is dead,
  // and the screen that receives it has no way to sign out.
  const handleUnauthorized = useCallback(() => {
    void tokenStore.clear().catch(() => undefined);
    setSession(SIGNED_OUT);
  }, [tokenStore]);
  useUnauthorizedBinding(handleUnauthorized);

  const clearKeyspaces = useCallback(
    (keyspaces: readonly string[], workspaceId: WorkspaceId | null) => {
      for (const root of keyspacesToQueryRoots(keyspaces, workspaceId)) {
        void queryClient.cancelQueries({ queryKey: root });
      }
      // Cancel first: a response that lands after a switch would write into a cache the new
      // identity is about to read, and the plan decides which fetches to abort.
      //
      // Then remove EVERY workspace-scoped root, deliberately wider than the plan. Removal
      // is what guarantees nothing from the previous identity survives, and the asymmetry
      // settles the direction: under-removal leaks one identity's data into another, while
      // over-removal costs a refetch. The plan's precision therefore governs cancellation,
      // not this step -- an earlier comment implied it governed both.
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] === "workspace" });
    },
    [queryClient],
  );

  const signIn = useCallback<SessionContextValue["signIn"]>(
    async ({ token, account, workspaces }) => {
      await tokenStore.write(token);
      clearKeyspaces(planAccountSwitch(null, account).keyspaces, null);
      setSession({
        account,
        workspaces,
        workspace: defaultWorkspaceFor(account, workspaces),
        accounts: [account],
        status: "signed_in",
      });
    },
    [clearKeyspaces, tokenStore],
  );

  const signOut = useCallback(async () => {
    const previousWorkspace = session.workspace?.workspaceId ?? null;
    // Revoke server-side first so a copied token dies with the sign-out, not
    // with its expiry. Best-effort: an offline client must still sign out.
    try {
      await client.auth.logout();
    } catch {
      // Local state below is the source of truth for signed-out.
    }
    await tokenStore.clear();
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] === "workspace" });
    void previousWorkspace;
    setSession(SIGNED_OUT);
  }, [client, queryClient, session.workspace?.workspaceId, tokenStore]);

  const switchAccount = useCallback<SessionContextValue["switchAccount"]>(
    async (account) => {
      // A switch may only re-select an identity this session already authenticated as.
      //
      // The previous implementation adopted whatever account it was handed: a new display
      // name over the SAME bearer token, and it mapped the new account onto the previous
      // account's workspace list. The header would then show one person while every request
      // on the wire carried another's credential. Nothing calls this yet -- the account list
      // holds exactly one entry -- so it was a footgun rather than a hole, and refusing the
      // unauthenticated case is what keeps it that way if someone wires it up.
      if (session.account === null || account.identityId !== session.account.identityId) {
        throw new Error(
          "switching to another account needs that account's own credentials; this build has no account-linking flow",
        );
      }
      const plan = planAccountSwitch(session.account, account);
      clearKeyspaces(plan.keyspaces, session.workspace?.workspaceId ?? null);
      setSession((previous) => ({
        ...previous,
        account,
        workspace: defaultWorkspaceFor(account, previous.workspaces),
      }));
    },
    [clearKeyspaces, session.account, session.workspace?.workspaceId],
  );

  const switchWorkspace = useCallback<SessionContextValue["switchWorkspace"]>(
    async (workspaceId) => {
      const next = session.workspaces.find((workspace) => workspace.workspaceId === workspaceId);
      if (!next) throw new Error(`workspace ${workspaceId} is not available to this account`);
      // Server first: membership and plane are re-checked there, and the
      // session row is what every later request authenticates against. Local
      // state only moves after the server confirms the switch.
      await client.auth.switchWorkspace(workspaceId);
      const plan = planWorkspaceSwitch(session.workspace, next);
      clearKeyspaces(plan.keyspaces, session.workspace?.workspaceId ?? null);
      setSession((previous) => ({ ...previous, workspace: next }));
    },
    [clearKeyspaces, client, session.workspace, session.workspaces],
  );

  const setAccounts = useCallback((accounts: readonly SessionAccount[]) => {
    setSession((previous) => ({ ...previous, accounts }));
  }, []);

  // Hydrate a persisted session on mount (report.md F-005): without this, a
  // reload silently signed the user out even though the token store still held
  // a valid token. The server is authoritative — an expired or revoked token
  // clears the store and stays signed out.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const token = await tokenStore.read().catch(() => null);
      if (!token || cancelled) return;
      try {
        const snapshot = await client.auth.session();
        if (cancelled) return;
        if (!snapshot.user) {
          await tokenStore.clear();
          return;
        }
        const account: SessionAccount = {
          identityId: snapshot.identityId ?? snapshot.user.userId,
          label: snapshot.user.displayName ?? snapshot.user.primaryEmail ?? "You",
          plane: snapshot.plane ?? "personal",
          email: snapshot.user.primaryEmail,
          userId: snapshot.user.userId,
        };
        const workspaces: SessionWorkspace[] = snapshot.workspaces.map((workspace) => ({
          workspaceId: workspace.workspaceId as WorkspaceId,
          name: workspace.name,
          kind: workspace.kind,
          plan: workspace.plan,
          planStatus: workspace.planStatus,
          role: workspace.role,
        }));
        const active = workspaces.find((workspace) => workspace.workspaceId === snapshot.activeWorkspaceId)
          ?? defaultWorkspaceFor(account, workspaces);
        clearKeyspaces(planAccountSwitch(null, account).keyspaces, null);
        setSession({ account, workspace: active, workspaces, accounts: [account], status: "signed_in" });
      } catch (error) {
        if (cancelled) return;
        // Only a rejected credential clears the store. A network failure or a server error
        // says nothing about the token, and treating it as one destroyed a valid session on
        // any offline reload: the comment above describes the narrow rule, but a bare catch
        // implemented the wide one. Leaving the store alone keeps the session recoverable
        // by the next successful load.
        if (error instanceof DayviseApiError && error.status === 401) {
          await tokenStore.clear().catch(() => undefined);
          setSession(SIGNED_OUT);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, clearKeyspaces, tokenStore]);

  const value = useMemo<SessionContextValue>(
    () => ({ session, signIn, signOut, switchAccount, switchWorkspace, setAccounts }),
    [session, signIn, signOut, switchAccount, switchWorkspace, setAccounts],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (value === null) throw new Error("useSession must be used inside <SessionProvider>");
  return value;
}

/** The workspace id every request must carry. `null` while signed out. */
export function useActiveWorkspaceId(): WorkspaceId | null {
  return useSession().session.workspace?.workspaceId ?? null;
}
